import React, { useRef, useState } from 'react'
import {
  Autocomplete,
  Box,
  Grid,
  TextField,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormGroup,
  FormControlLabel,
  Alert,
  IconButton,
} from '@mui/material'
import { PostContentProps } from 'components/PostScheduling/PostContent'
import { Platform, useMediaAccountsQuery } from 'graphql/generated/graphql'
import {
  DndContext,
  closestCenter,
  PointerSensor,
  useSensor,
  useSensors,
  DragEndEvent,
} from '@dnd-kit/core'
import {
  arrayMove,
  SortableContext,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Facebook, Image as ImageIcon, Video, Lock, Users, X, Upload } from 'lucide-react'
import MUICaptionWithEmojis from '../../MUICaptionWithEmojis'

interface MediaFile {
  file: File
  type: 'image' | 'video'
}

function SortableMediaItem({ id, children }: { id: string; children: React.ReactNode }) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id })
  const style = { 
    transform: CSS.Transform.toString(transform), 
    transition, 
    cursor: 'grab',
    opacity: isDragging ? 0.5 : 1,
  }
  
  return (
    <Box sx={{ position: 'relative' }}>
      {/* Invisible drag handle */}
      <Box
        ref={setNodeRef}
        {...attributes}
        {...listeners}
        sx={{
          ...style,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          zIndex: 1,
        }}
      />
      {/* Content */}
      <Box sx={{ position: 'relative', zIndex: 2 }}>
        {children}
      </Box>
    </Box>
  )
}

export default function ModernFacebookPostContent({
  setProfileValue,
  imageFiles,
  setImageFiles,
  captionRef,
  videoFiles,
  setVideoFiles,
  postTypeValue,
  setPostTypeValue,
}: PostContentProps): JSX.Element {
  const { data, loading } = useMediaAccountsQuery({ variables: { platform: Platform.Facebook } })
  const [privacyLevel, setPrivacyLevel] = useState<string>('PUBLIC')
  const [allowTagging, setAllowTagging] = useState(true)
  const [brandedContent, setBrandedContent] = useState(false)
  const inputRef = useRef<HTMLInputElement | null>(null)

  const combinedFiles: MediaFile[] = [
    ...imageFiles.map((f) => ({ file: f, type: 'image' as const })),
    ...videoFiles.map((f) => ({ file: f, type: 'video' as const })),
  ]

  const setCombinedFiles = (newList: MediaFile[]) => {
    setImageFiles(newList.filter((m) => m.type === 'image').map((m) => m.file))
    setVideoFiles(newList.filter((m) => m.type === 'video').map((m) => m.file))
  }

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // FIXED: Prevents accidental drags
      },
    })
  )

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = combinedFiles.findIndex((m) => `${m.file.name}-${m.file.lastModified}` === active.id)
    const newIndex = combinedFiles.findIndex((m) => `${m.file.name}-${m.file.lastModified}` === over.id)
    setCombinedFiles(arrayMove(combinedFiles, oldIndex, newIndex))
  }

  const handleRemove = (index: number) => {
    const newList = [...combinedFiles]
    newList.splice(index, 1)
    setCombinedFiles(newList)
  }

  const handleFiles = (newFiles: FileList | null) => {
    if (!newFiles) return
    const isVideo = postTypeValue === 'video'
    const acceptedTypes = isVideo
      ? ['video/mp4', 'video/mov', 'video/quicktime']
      : ['image/jpeg', 'image/png', 'image/webp']
    const validFiles: MediaFile[] = Array.from(newFiles)
      .filter((file) => {
        if (!acceptedTypes.includes(file.type)) {
          alert(`Unsupported file type: ${file.type}`)
          return false
        }
        return true
      })
      .map((f) => ({ file: f, type: isVideo ? 'video' : 'image' }))
    setCombinedFiles([...combinedFiles, ...validFiles])
  }

  return (
    <Box sx={{ color: '#fff' }}>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 2,
            background: 'linear-gradient(135deg, #1877f2 0%, #0c5fcd 100%)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <Facebook size={24} color="#fff" />
        </Box>
        <Box>
          <Typography sx={{ fontSize: 24, fontWeight: 600, color: '#fff' }}>Facebook Post</Typography>
          <Typography sx={{ fontSize: 14, color: 'rgba(255,255,255,0.6)' }}>
            Create content for your Facebook page
          </Typography>
        </Box>
      </Box>

      {/* Account Selection */}
      <Box sx={{ mb: 3, p: 3, borderRadius: 2, border: '1px solid rgba(168,85,247,0.15)', background: 'rgba(30,41,59,0.4)' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 2, color: '#fff' }}>Select Account</Typography>
        <Autocomplete
          loading={loading}
          options={data?.mediaAccounts ?? []}
          getOptionLabel={(option) => option.accountName}
          onChange={(e, value) => value && setProfileValue(value)}
          renderInput={(params) => (
            <TextField 
              {...params} 
              label="Choose your Facebook page"
              sx={{
                '& .MuiOutlinedInput-root': {
                  '& fieldset': { borderColor: 'rgba(168, 85, 247, 0.3)' },
                  '&:hover fieldset': { borderColor: 'rgba(168, 85, 247, 0.5)' },
                  '&.Mui-focused fieldset': { borderColor: '#a855f7' },
                  backgroundColor: 'rgba(15, 23, 42, 0.5)',
                },
                '& .MuiInputLabel-root': { color: 'rgba(255, 255, 255, 0.7)' },
                '& .MuiInputBase-input': { color: '#fff' },
              }}
            />
          )}
          sx={{
            '& .MuiAutocomplete-endAdornment': {
              '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' },
            },
          }}
        />
        {data?.mediaAccounts.length === 0 && !loading && (
          <Box sx={{ mt: 2, p: 1.5, background: 'rgba(239, 68, 68, 0.1)', border: '1px solid rgba(239, 68, 68, 0.3)', borderRadius: 1, color: '#fca5a5', fontSize: 14 }}>
            ⚠️ Please connect your Facebook business account first.
          </Box>
        )}
      </Box>

      {/* Post Type */}
      <Box sx={{ mb: 3, p: 3, borderRadius: 2, border: '1px solid rgba(168,85,247,0.15)', background: 'rgba(30,41,59,0.4)' }}>
        <Typography sx={{ fontSize: 16, fontWeight: 600, mb: 2, color: '#fff' }}>Content Type</Typography>
        <ToggleButtonGroup
          value={postTypeValue}
          exclusive
          onChange={(e, value) => value && setPostTypeValue?.(value)}
          sx={{
            gap: 2,
            '& .MuiToggleButtonGroup-grouped': {
              border: 'none',
              borderRadius: '12px !important',
            },
            '& .MuiToggleButton-root': {
              borderRadius: 2,
              color: 'rgba(255,255,255,0.7)',
              borderColor: 'rgba(168,85,247,0.3)',
              backgroundColor: 'rgba(15,23,42,0.5)',
              padding: '10px 20px',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              '&.Mui-selected': {
                backgroundColor: 'rgba(168,85,247,0.2)',
                color: '#fff',
                borderColor: '#a855f7',
              },
              '&:hover': {
                backgroundColor: 'rgba(168,85,247,0.1)',
              },
            },
          }}
        >
          <ToggleButton value="image">
            <ImageIcon size={18} /> Image
          </ToggleButton>
          <ToggleButton value="video">
            <Video size={18} /> Video
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      {/* Upload Section */}
      <Box
        onClick={() => inputRef.current?.click()}
        sx={{
          border: '2px dashed rgba(168,85,247,0.4)',
          borderRadius: 3,
          p: 3,
          textAlign: 'center',
          mb: 3,
          cursor: 'pointer',
          background: 'rgba(15, 23, 42, 0.5)',
          '&:hover': {
            borderColor: 'rgba(168, 85, 247, 0.7)',
            background: 'rgba(31, 41, 55, 0.6)',
          },
        }}
      >
        <input
          ref={inputRef}
          type="file"
          multiple
          accept={postTypeValue === 'video' ? 'video/*' : 'image/*'}
          onChange={(e) => handleFiles(e.target.files)}
          style={{ display: 'none' }}
        />
        <Upload size={28} color="#a855f7" />
        <Typography sx={{ color: '#a855f7', mt: 1, fontWeight: 500 }}>
          Upload {postTypeValue === 'video' ? 'Video' : 'Images'}
        </Typography>

        {combinedFiles.length > 0 && (
          <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
            <SortableContext
              items={combinedFiles.map((m) => `${m.file.name}-${m.file.lastModified}`)}
              strategy={verticalListSortingStrategy}
            >
              <Grid container spacing={1.5} sx={{ mt: 2, justifyContent: 'center' }}>
                {combinedFiles.map((media, index) => (
                  <Grid item key={`${media.file.name}-${media.file.lastModified}`}>
                    <SortableMediaItem id={`${media.file.name}-${media.file.lastModified}`}>
                      <Box
                        sx={{
                          position: 'relative',
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '2px solid rgba(168,85,247,0.3)',
                          width: 120,
                          height: 120,
                        }}
                      >
                        {media.type === 'image' ? (
                          <Box
                            component="img"
                            src={URL.createObjectURL(media.file)}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                          />
                        ) : (
                          <Box
                            component="video"
                            src={URL.createObjectURL(media.file)}
                            sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                            muted
                            loop
                          />
                        )}
                        <Box
                          sx={{
                            position: 'absolute',
                            top: 4,
                            left: 4,
                            background: 'rgba(0,0,0,0.7)',
                            color: '#fff',
                            px: 1,
                            borderRadius: 1,
                            fontSize: 12,
                            fontWeight: 600,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            zIndex: 3,
                          }}
                        >
                          {index + 1}
                          {media.type === 'image' ? <ImageIcon size={12} /> : <Video size={12} />}
                        </Box>
                        <IconButton
                          onClick={(e) => {
                            e.preventDefault()
                            e.stopPropagation()
                            handleRemove(index)
                          }}
                          sx={{
                            position: 'absolute',
                            top: 4,
                            right: 4,
                            background: 'rgba(0,0,0,0.7)',
                            '&:hover': { 
                              background: 'rgba(239, 68, 68, 0.9)',
                              transform: 'scale(1.1)',
                            },
                            width: 28,
                            height: 28,
                            zIndex: 10, // FIXED: Higher z-index
                            transition: 'all 0.2s ease',
                          }}
                        >
                          <X size={16} color="#fff" />
                        </IconButton>
                      </Box>
                    </SortableMediaItem>
                  </Grid>
                ))}
              </Grid>
            </SortableContext>
          </DndContext>
        )}
      </Box>

      {/* Privacy */}
      <Box sx={{ mb: 3, p: 3, borderRadius: 2, border: '1px solid rgba(168,85,247,0.15)', background: 'rgba(30,41,59,0.4)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Lock size={20} color="#a855f7" />
          <Typography sx={{ fontWeight: 600, color: '#fff' }}>Who can see this post?</Typography>
        </Box>
        <FormControl fullWidth>
          <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)', '&.Mui-focused': { color: '#a855f7' } }}>
            Privacy
          </InputLabel>
          <Select
            value={privacyLevel}
            label="Privacy"
            onChange={(e) => setPrivacyLevel(e.target.value)}
            sx={{
              color: '#fff',
              backgroundColor: 'rgba(15,23,42,0.5)',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(168,85,247,0.3)' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'rgba(168,85,247,0.5)' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: '#a855f7' },
              '& .MuiSvgIcon-root': { color: 'rgba(255, 255, 255, 0.7)' },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  bgcolor: 'rgba(30, 41, 59, 0.95)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(168, 85, 247, 0.2)',
                  color: '#fff',
                  '& .MuiMenuItem-root': {
                    color: '#fff',
                    '&:hover': { bgcolor: 'rgba(168, 85, 247, 0.1)' },
                    '&.Mui-selected': { 
                      bgcolor: 'rgba(168, 85, 247, 0.2)',
                      '&:hover': { bgcolor: 'rgba(168, 85, 247, 0.3)' },
                    },
                  },
                },
              },
            }}
          >
            <MenuItem value="PUBLIC">Public</MenuItem>
            <MenuItem value="FRIENDS">Friends</MenuItem>
            <MenuItem value="ONLY_ME">Only Me</MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Tagging */}
      <Box sx={{ mb: 3, p: 3, borderRadius: 2, border: '1px solid rgba(168,85,247,0.15)', background: 'rgba(30,41,59,0.4)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Users size={20} color="#a855f7" />
          <Typography sx={{ fontWeight: 600, color: '#fff' }}>Post Permissions</Typography>
        </Box>
        <FormGroup>
          <FormControlLabel
            control={
              <Switch 
                checked={allowTagging} 
                onChange={(e) => setAllowTagging(e.target.checked)}
                sx={{
                  '& .MuiSwitch-switchBase.Mui-checked': { color: '#a855f7' },
                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(168, 85, 247, 0.5)' },
                }}
              />
            }
            label="Allow tagging of other pages or users"
            sx={{ '.MuiFormControlLabel-label': { color: '#fff' } }}
          />
        </FormGroup>
      </Box>

      {/* Branded Content */}
      <Box sx={{ mb: 3, p: 3, borderRadius: 2, border: '1px solid rgba(168,85,247,0.15)', background: 'rgba(30,41,59,0.4)' }}>
        <FormControlLabel
          control={
            <Switch 
              checked={brandedContent} 
              onChange={(e) => setBrandedContent(e.target.checked)}
              sx={{
                '& .MuiSwitch-switchBase.Mui-checked': { color: '#a855f7' },
                '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'rgba(168, 85, 247, 0.5)' },
              }}
            />
          }
          label="Mark as Branded Content / Paid Partnership"
          sx={{ '.MuiFormControlLabel-label': { color: '#fff', fontWeight: 600 }, mb: 1 }}
        />
        {brandedContent && (
          <Alert
            severity="info"
            sx={{
              mt: 1,
              backgroundColor: 'rgba(59,130,246,0.1)',
              border: '1px solid rgba(59,130,246,0.3)',
              color: '#93c5fd',
              '& .MuiAlert-icon': { color: '#93c5fd' },
            }}
          >
            Your post will be labeled as Branded Content / Paid Partnership. This cannot be changed
            once published.
          </Alert>
        )}
      </Box>
        {/* Caption with Emoji Support */}
        <MUICaptionWithEmojis
          captionRef={captionRef}
          placeholder="Write your caption... Don't forget the hashtags! #Instagram"
          maxLength={2200}
          rows={5}
        />
    </Box>
  )
}